import Time from '@/utils/Time';
import API from '@/services/api';
import { Form } from '@CS/routes';
import { connect } from 'react-redux';
import FONTS from '@/assets/fonts.json';
// import { sentryDSN } from '@/utils/API';
import { User, MCD } from '@/services/app';
import { useState, useEffect } from 'react';
import Images, { Logo } from '@/assets/images';

import { getLogged } from '@/redux/auth/selectors';
import type { UserState } from '@/redux/user/types';
import { RequestError } from '@CS/components/Popups';
import { getUserState } from '@/redux/user/selectors';
import { showConsole, isDEV } from '@/utils/Location';
import { useRecaptcha, useNavigateQuery } from '@/hooks';
import type { RequestErrorInfo } from '@/services/api/types';
import { Routes, Route, useLocation } from 'react-router-dom';
import { /* Login, */ Home, Quizzes, Game, Done } from '@/routes';
// import { withSentryReactRouterV6Routing } from '@sentry/react';
import TranslationProvider from '@CS/components/TranslationProvider';
import { useFonts, useImages, useLoader, useNetworkListener } from '@CS/hooks';

type ApplicationProps = {
  logged?: boolean;
  login?: boolean;
  user: UserState;
};

const Application = ({ user, logged, login = true }: ApplicationProps) => {
  const { search } = useLocation();
  const navigate = useNavigateQuery();
  const fontsLoaded = useFonts(FONTS);

  const network = useNetworkListener();
  const { recaptcha } = useRecaptcha();
  const onLoad = () => setLoading(false);
  const [loading, setLoading] = useState(true);

  // Avoid re-mapping images when they are already loaded:
  const LocalizedImages = !(
    window as Application & { IMAGES: Record<string, HTMLImageElement> }
  ).IMAGES &&
      Object.entries(Images).reduce((images, [key, { ar, en }]) =>
        Object.assign(images, { [`${key}AR`]: ar }, { [`${key}EN`]: en })
      , {}) || {};

  const cancelLogin = () => setLoginError(void 0);
  const { email, points, country, readonlyData } = user ?? {};
  const imagesLoaded = useImages({ Logo, ...LocalizedImages });
  const [loginError, setLoginError] = useState<RequestErrorInfo>();
  const loader = useLoader(loading || !recaptcha || !fontsLoaded || !imagesLoaded);

  const setDocumentLanguage = ({ language = 'en', country = 'uae' }) =>
    ~document.documentElement.setAttribute(
      'lang', `${language}-${country.slice(-2).toLocaleUpperCase()}`
    ) &&

    ~document.documentElement.setAttribute(
      'dir', language === 'ar' && 'rtl' || 'ltr'
    );

  const toggleFullscreen = () =>
    !isDEV() && ~MCD.toggleFullscreen() && MCD.orientationScreen();

  const onVisibilityChange = () =>
    document.visibilityState === 'visible' && toggleFullscreen();

  const getUserData = () =>
    Promise.all([User.checkUserPoints(), User.checkUserData()]);

  const handleAuth = () =>
    (!email || typeof points !== 'number') &&
    getUserData().then(onLoad).catch(loginRedirect);

  const initApplication = () => {
    API.revokeToken();
    toggleFullscreen();
    User.checkUserCountry();
    User.checkUserLanguage();
    login ? handleAuth() : onLoad();
  };

  const loginRedirect = () => {
    setLoading(false);
    // navigate('/login');

    MCD.promptLogin()
      .then(() => onLogin())
      .catch((error) => {
        const message = 'Prompt login error';
        setLoginError(error && { message, ...error } || { error: message });
      });
  };

  const onLogin = async () => {
    toggleFullscreen();
    await getUserData();
    setLoading(false);
    navigate('/');
  };

  useEffect(() => {
    setDocumentLanguage(user);
    const userInfo = country && email;
    const ready = recaptcha && !logged;

    ready &&
      userInfo &&
      API.login(user).catch(error =>
        setLoginError(API.getRequestErrorInfo(error))
      );
  }, [recaptcha, country, logged, email, user]);

  useEffect(() => {
    showConsole((readonlyData as { console?: boolean })?.console);
  }, [readonlyData]);

  useEffect(() => {
    MCD.App.mcd && !loading && initApplication();
  }, [MCD.App.mcd]);

  useEffect(() => {
    Time.setCurrentTime(search);
  }, [search]);

  useEffect(() => {
    document.addEventListener('visibilitychange', onVisibilityChange);
    document.addEventListener('mcdBridgeReady', initApplication);
    (MCD.App.mcd || isDEV()) && initApplication();

    return () => {
      document.removeEventListener('mcdBridgeReady', initApplication);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  /**
   * Sentry Tracing Routes Wrapper:
   *
  const RoutesWrapper = sentryDSN
    ? withSentryReactRouterV6Routing(Routes)
    : Routes; */

  return (
    loader ||
    network || (
      <TranslationProvider lang={user.language}>
        <Routes>
          {/* <RoutesWrapper> */}
          {/* <Route path="/login" element={<Login onLogin={onLogin} />} /> */}
          <Route path="/" element={<Home />} />
          <Route path="/quizzes" element={<Quizzes dailyQuizzes={3} />} />
          <Route path="/game/:index" element={<Game />} />
          <Route path="/form" element={<Form user={user} />} />
          <Route path="/done" element={<Done />} />
          {/* </RoutesWrapper> */}
        </Routes>

        {loginError && <RequestError info={loginError} onClose={cancelLogin} />}
      </TranslationProvider>
    )
  );
};

const mapStateToProps = (state: never) => ({
  user: getUserState(state),
  logged: getLogged(state)
});

export default connect(mapStateToProps)(Application);
